import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { playersAPI, weightAPI, attendanceAPI, injuriesAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, User, Scale, CalendarCheck, Heart, Target, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatDate, getStatusColor, getStatusBgColor } from '../lib/utils';

export default function PlayerProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [weightHistory, setWeightHistory] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [playerRes, weightRes, attendanceRes, injuriesRes] = await Promise.all([
        playersAPI.getOne(id),
        weightAPI.getByPlayer(id),
        attendanceAPI.getByPlayer(id),
        injuriesAPI.getByPlayer(id)
      ]);
      
      setPlayer(playerRes.data);
      setWeightHistory(weightRes.data);
      setAttendanceHistory(attendanceRes.data);
      setInjuries(injuriesRes.data);
    } catch (error) {
      console.error('Failed to load player data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-[#71717A]">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!player) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 text-[#71717A]">
          <p className="text-lg">Player not found</p>
          <Button onClick={() => navigate('/roster')} className="mt-4">
            Back to Roster
          </Button>
        </div>
      </Layout>
    );
  }

  // Calculate stats
  const attendanceRate = attendanceHistory.length > 0
    ? Math.round((attendanceHistory.filter(a => a.status === 'present').length / attendanceHistory.length) * 100)
    : 0;

  const weightChange = weightHistory.length >= 2
    ? (weightHistory[weightHistory.length - 1].weight - weightHistory[0].weight).toFixed(1)
    : null;

  const activeInjuries = injuries.filter(i => i.status !== 'cleared').length;

  // Prepare chart data
  const chartData = weightHistory.slice(-30).map(log => ({
    date: formatDate(log.date).split(',')[0], // Just month and day
    weight: log.weight
  }));

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/roster')}
          className="text-[#A1A1AA] hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Roster
        </Button>

        {/* Player Header */}
        <div className="flex flex-col lg:flex-row gap-6" data-testid="player-profile">
          {/* Player Info Card */}
          <Card className="lg:w-80 bg-[#121212] border-[#27272A]">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-[#27272A] flex items-center justify-center mb-4">
                  <User className="w-12 h-12 text-[#71717A]" />
                </div>
                <span className="text-[#FF3B30] font-display text-2xl">#{player.jersey_number}</span>
                <h1 className="font-display text-3xl text-white mt-2">{player.name.toUpperCase()}</h1>
                <p className="text-[#A1A1AA] mt-1">{player.position}</p>
                <span className={`mt-3 px-3 py-1 text-sm font-medium border ${getStatusBgColor(player.status)} ${getStatusColor(player.status)}`}>
                  {player.status.toUpperCase()}
                </span>
              </div>

              <div className="mt-6 pt-6 border-t border-[#27272A] space-y-4">
                {player.locker_number && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Locker/Loop #</span>
                    <span className="text-[#FF3B30] font-bold">{player.locker_number}</span>
                  </div>
                )}
                {player.group_number && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Group #</span>
                    <span className="text-white">{player.group_number}</span>
                  </div>
                )}
                {player.height && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Height</span>
                    <span className="text-white">{player.height}</span>
                  </div>
                )}
                {player.current_weight && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Current Weight</span>
                    <span className="text-white">{player.current_weight} lbs</span>
                  </div>
                )}
                {player.target_weight && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Target Weight</span>
                    <span className="text-white">{player.target_weight} lbs</span>
                  </div>
                )}
                
                {/* Contact Section */}
                <div className="pt-4 border-t border-[#27272A]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-3">Contact Info</p>
                  {player.phone && (
                    <div className="flex justify-between mb-2">
                      <span className="text-[#71717A]">Player Phone</span>
                      <span className="text-white">{player.phone}</span>
                    </div>
                  )}
                  {player.parent_name && (
                    <div className="flex justify-between mb-2">
                      <span className="text-[#71717A]">Parent Name</span>
                      <span className="text-white">{player.parent_name}</span>
                    </div>
                  )}
                  {player.parent_phone && (
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">Parent Phone</span>
                      <span className="text-white">{player.parent_phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats & Charts */}
          <div className="flex-1 space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[#121212] border-[#27272A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#32D74B]/10 flex items-center justify-center">
                      <CalendarCheck className="w-5 h-5 text-[#32D74B]" />
                    </div>
                    <div>
                      <p className="font-display text-2xl text-white">{attendanceRate}%</p>
                      <p className="text-xs text-[#71717A]">Attendance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#121212] border-[#27272A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#007AFF]/10 flex items-center justify-center">
                      <Scale className="w-5 h-5 text-[#007AFF]" />
                    </div>
                    <div>
                      <p className="font-display text-2xl text-white">{player.current_weight || '-'}</p>
                      <p className="text-xs text-[#71717A]">Current (lbs)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#121212] border-[#27272A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FFD60A]/10 flex items-center justify-center">
                      {weightChange && parseFloat(weightChange) >= 0 
                        ? <TrendingUp className="w-5 h-5 text-[#32D74B]" />
                        : <TrendingDown className="w-5 h-5 text-[#FF3B30]" />
                      }
                    </div>
                    <div>
                      <p className={`font-display text-2xl ${
                        weightChange && parseFloat(weightChange) >= 0 ? 'text-[#32D74B]' : 'text-[#FF3B30]'
                      }`}>
                        {weightChange ? `${parseFloat(weightChange) >= 0 ? '+' : ''}${weightChange}` : '-'}
                      </p>
                      <p className="text-xs text-[#71717A]">Weight Change</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#121212] border-[#27272A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30]/10 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#FF3B30]" />
                    </div>
                    <div>
                      <p className="font-display text-2xl text-white">{activeInjuries}</p>
                      <p className="text-xs text-[#71717A]">Active Injuries</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Weight Chart */}
            <Card className="bg-[#121212] border-[#27272A]" data-testid="weight-progress-chart">
              <CardHeader className="border-b border-[#27272A]">
                <CardTitle className="flex items-center gap-2 text-white">
                  <TrendingUp className="w-5 h-5 text-[#007AFF]" />
                  Weight Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {chartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <XAxis 
                          dataKey="date" 
                          stroke="#71717A" 
                          tick={{ fill: '#71717A', fontSize: 12 }}
                          axisLine={{ stroke: '#27272A' }}
                        />
                        <YAxis 
                          stroke="#71717A" 
                          tick={{ fill: '#71717A', fontSize: 12 }}
                          axisLine={{ stroke: '#27272A' }}
                          domain={['dataMin - 5', 'dataMax + 5']}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1A1A1A', 
                            border: '1px solid #27272A',
                            borderRadius: 0 
                          }}
                          labelStyle={{ color: '#FFFFFF' }}
                        />
                        {player.target_weight && (
                          <ReferenceLine 
                            y={player.target_weight} 
                            stroke="#FFD60A" 
                            strokeDasharray="3 3"
                            label={{ value: 'Target', fill: '#FFD60A', fontSize: 12 }}
                          />
                        )}
                        <Line 
                          type="linear" 
                          dataKey="weight" 
                          stroke="#FF3B30" 
                          strokeWidth={2}
                          dot={{ fill: '#FF3B30', r: 4 }}
                          activeDot={{ r: 6, fill: '#FF3B30' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-[#71717A]">
                    No weight data recorded yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Injury History */}
        <Card className="bg-[#121212] border-[#27272A]" data-testid="injury-history">
          <CardHeader className="border-b border-[#27272A]">
            <CardTitle className="flex items-center gap-2 text-white">
              <Heart className="w-5 h-5 text-[#FF3B30]" />
              Injury History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {injuries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Body Part</th>
                      <th>Severity</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {injuries.map((injury) => (
                      <tr key={injury.id}>
                        <td className="text-white">{formatDate(injury.injury_date)}</td>
                        <td className="text-[#A1A1AA]">{injury.injury_type}</td>
                        <td className="text-[#A1A1AA]">{injury.body_part}</td>
                        <td>
                          <span className={`text-sm ${getStatusColor(injury.severity)}`}>
                            {injury.severity.charAt(0).toUpperCase() + injury.severity.slice(1)}
                          </span>
                        </td>
                        <td>
                          <span className={`px-2 py-1 text-xs font-medium border ${getStatusBgColor(injury.status)} ${getStatusColor(injury.status)}`}>
                            {injury.status.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-[#71717A]">
                <Heart className="w-12 h-12 mb-4 opacity-50" />
                <p>No injury history</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
